// GAVION ai-email (spec §6): proposed drafts as the UI shows them. Text only (the full email in the confirm dialog
// is rendered as text, never as HTML).
import { AI_AFTER_CALL_OUTCOMES } from "../../../../../supabase/functions/_shared/automation/aiRules.ts";
import type { DraftTrigger } from "../../../../../supabase/functions/_shared/ai/types.ts";
import {
  FOLLOW_UP_CALL_OUTCOMES,
  type FollowUpSettings,
} from "../../../../../supabase/functions/_shared/ai/followUp.ts";

export type AiDraftRow = {
  id: number;
  company_id: number;
  contact_id: number | null;
  deal_id: number | null;
  sales_id: number;
  trigger: DraftTrigger;
  to_email: string | null;
  /** null on rows that never got text (superseded, failed): only pending rows are listed, but never assume. */
  subject: string | null;
  body: string | null;
  rationale: string | null;
  language: "fr" | "en" | null;
  status: string;
  created_at: string;
  expires_at: string | null;
  /** GAVION ai follow-up: « Suivi — touche n de m » (follow_up drafts only). */
  follow_up_position?: number | null;
  follow_up_total?: number | null;
};
/** Listed drafts: pending proposed drafts (on-demand rows only link a sent email). */
export const PENDING_FILTER = {
  status: "pending",
  "trigger@neq": "on_demand",
} as const;

const decode = (s: string) =>
  s
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");

/** Plain text of a draft: a blank line between paragraphs (and around a list), one line per list item or line break. */
export function draftText(html: string | null | undefined): string {
  if (!html) return "";
  return decode(
    html
      .replace(/<\/p>|<\/[uo]l>/gi, "\n\n")
      .replace(/<br\s*\/?>|<\/li>/gi, "\n")
      .replace(/<[^>]*>/g, ""),
  )
    .split("\n")
    .map((l) => l.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function draftPreview(
  html: string | null | undefined,
  max = 240,
): string {
  // compact: one line per paragraph, no blank lines (the card shows them with whitespace-pre-line)
  const text = draftText(html).replace(/\n{2,}/g, "\n");
  const cps = Array.from(text);
  return cps.length <= max ? text : `${cps.slice(0, max).join("").trimEnd()} …`;
}

/** The dialer toast (spec §6): a qualifying outcome while R20 (« Après un appel → brouillon IA ») is on. */
export function aiFollowUpExpected(
  outcome: string,
  rules: readonly { key?: unknown; enabled?: boolean | null }[],
): boolean {
  return (
    (AI_AFTER_CALL_OUTCOMES as readonly string[]).includes(outcome) &&
    rules.some((r) => r.key === "R20" && r.enabled !== false)
  );
}

/** GAVION ai follow-up: the dialer toast after a call that plans a follow-up (SQL: private.ai_follow_up_on_call). */
export function aiFollowUpPlanned(
  outcome: string,
  s: FollowUpSettings,
): boolean {
  return s.enabled && Object.hasOwn(FOLLOW_UP_CALL_OUTCOMES, outcome);
}

/**
 * GAVION ai follow-up: what the dialer says after a saved call. A planning outcome → « Suivi IA planifié » (R20 then
 * skips the lead: ai_enqueue_draft answers ineligible:follow_up_planned); otherwise R20's toast as before, and the demo
 * writes R20's draft (`afterCallDraft`). Settings unknown (the configuration could not be read): no toast at all, since
 * neither « planifié » nor R20's draft can be promised.
 */
export function dialerAiStep(
  outcome: string,
  s: FollowUpSettings | null,
  rules: readonly { key?: unknown; enabled?: boolean | null }[],
): { toast: string | null; afterCallDraft: boolean } {
  if (s === null)
    return { toast: null, afterCallDraft: aiFollowUpExpected(outcome, rules) };
  if (aiFollowUpPlanned(outcome, s))
    return { toast: "gavion.ai.follow_up.dialer_toast", afterCallDraft: false };
  const r20 = aiFollowUpExpected(outcome, rules);
  return { toast: r20 ? "gavion.ai.dialer_toast" : null, afterCallDraft: r20 };
}
